import type {
  EnhancedLegacyManCode,
  EnhancedLegacyNationalProductGroup,
  EnhancedLegacyProductGroup,
  EnhancedTg3ToMaicConversionResponse,
} from '#api/enhancements/enhancedTg3Payload'
import type {
  EnhancedApiConfigurationStep,
  EnhancedProductFamily,
  EnhancedProductFamilyId,
  EnhancedProductGroup,
  EnhancedProductVariant,
} from '#api/enhancements/enhancedTmsApiTypes.ts'
import type { AdminNatCodesCode, AdminNatCodesFamily, AdminNatCodesGroup } from '#api/tmsApiService'
import type { MarketDetails } from '#app/store/slices/configuration/marketSlice'
import type { FullApiConfiguration } from '#features/Configuration/ApiConfiguration'
import { mapTg3ConversionInsights } from '#features/LegacyTg3Code/mapTg3ConversionInsights'
import { convertPrintOptionsFromManCode } from '#features/PrintOptions/convertPrintOptions'
import type { ConfigurationVersion, EcmConfigurationVersion, TruckConfiguration } from '#store/slices/configurationSlice'
import { makeRequestConfigurationStep } from '#types/Configuration'
import { isValidDateString } from '#utils/DateString'

function convertNationalCodes(
  locale: string,
  nationalCodes: EnhancedLegacyNationalProductGroup[] | undefined,
  configuredFamilies: TruckConfiguration['configuredFamilies'],
) {
  if (!nationalCodes) {
    return { nationalCodeGroups: [] }
  }

  const nationalCodeGroups = nationalCodes.map(
    (group, index): AdminNatCodesGroup => ({
      groupId: `tg3-nat-group-${index}` as AdminNatCodesGroup['groupId'],
      label: { [locale]: group.title || 'No title' },
      families: group.nationalCodes?.map((code, index) => {
        const mappedFamily = {
          familyId: code.code ?? `${index}`,
          label: { [locale]: 'Unknown family' },
          codes: [
            {
              executionId: code.code ?? `${index}`,
              codeId: code.code ?? `${index}`,
              label: {
                [locale]: code.text ?? 'No title',
              },
              status: 'published',
              attributes: {
                hideCode: code.hideCode || false,
                topOption: code.topOption,
                grossListPrice: code.grossListPrice,
                withoutCode: code.withoutCode || code.noCode || false,
                pictureIndex: code.pictureIndex,
                printingOptions: code.printingOptions,
                condition: code.condition ?? undefined,
                classification: code.classification ?? undefined,
                orderRelevant: code.orderRelevant,
                commissionRelevant: code.commissionRelevant,
                relevantForResidualValue: code.relevantForResidualValue,
                relevantForMaintenanceAndRepairBenefits: code.relevantForMaintenanceAndRepairBenefits,
              },
            } satisfies AdminNatCodesCode,
          ],
        } satisfies AdminNatCodesFamily

        let configuredFamily = configuredFamilies[mappedFamily.familyId as EnhancedProductFamilyId]
        if (!configuredFamily) {
          configuredFamilies[mappedFamily.familyId as EnhancedProductFamilyId] = configuredFamily = {
            isLocked: false,
            printOptions: {},
          }
        }
        const isSelected = !mappedFamily.codes[0].attributes.withoutCode && !mappedFamily.codes[0].attributes.hideCode
        if (isSelected) {
          configuredFamily.selectedVariant = {
            id: mappedFamily.codes[0].executionId as EnhancedProductVariant['id'],
            timestamp: Date.now(),
            previousSelection: configuredFamily?.selectedVariant?.id,
          }
        }
        configuredFamily.printOptions[mappedFamily.codes[0].executionId as EnhancedProductVariant['id']] = convertPrintOptionsFromManCode(
          mappedFamily.codes[0].attributes.printingOptions,
        )

        return mappedFamily
      }),
    }),
  )

  return { nationalCodeGroups }
}

function convertProductGroups(
  productGroups: EnhancedLegacyProductGroup[] | undefined,
  configuredFamilies: TruckConfiguration['configuredFamilies'] = {},
) {
  if (!productGroups) {
    return {
      productGroups: [],
    }
  }

  const groupById = new Map<EnhancedProductGroup['id'], EnhancedProductGroup>()
  const familyById = new Map<EnhancedProductFamily['id'], EnhancedProductFamily>()
  const variantById = new Map<EnhancedProductVariant['id'], EnhancedProductVariant>()
  const familyByVariantId = new Map<EnhancedProductVariant['id'], EnhancedProductFamily>()

  const mappedGroups = productGroups.map((group): EnhancedProductGroup => {
    const mappedGroup = {
      id: group.id as EnhancedProductGroup['id'],
      title: group.title,
      subGroups:
        group.subGroups?.map((subGroup): EnhancedProductGroup => {
          const mappedSubGroup = {
            id: subGroup.id as EnhancedProductGroup['id'],
            title: subGroup.title,
            families:
              subGroup.families?.map((family): EnhancedProductFamily => {
                const familyId = family.id as EnhancedProductFamily['id']
                const mappedFamily = {
                  id: familyId,
                  hasDetailData: family.hasDetailData,
                  totalVariantCount: family.totalVariantCount,
                  title: family.title ?? 'Missing title',
                  selectedVariantIndex: family.selectedVariantIndex,
                  selectionType: 'Default',
                  variants:
                    family.variants?.map((variant): EnhancedProductVariant => {
                      const mappedVariant = {
                        id: (variant.code || '-') as EnhancedProductVariant['id'],
                        title: variant.title ?? 'Missing title',
                        compatibility: 'unknown',
                        isSelected: variant.isSelected ?? true,
                        dummy_price: variant.grossListPrice,
                      } satisfies EnhancedProductVariant
                      variantById.set(mappedVariant.id, mappedVariant)

                      let familyOptions = configuredFamilies[familyId]
                      if (!familyOptions) {
                        configuredFamilies[familyId] = familyOptions = {
                          isLocked: false,
                          printOptions: {},
                        }
                      }
                      familyOptions.printOptions[mappedVariant.id] = convertPrintOptionsFromManCode(variant.printingOptions)
                      return mappedVariant
                    }) ?? [],
                } satisfies EnhancedProductFamily
                familyById.set(mappedFamily.id, mappedFamily)
                for (const mappedVariant of mappedFamily.variants) {
                  familyByVariantId.set(mappedVariant.id, mappedFamily)
                }

                return mappedFamily
              }) ?? [],
          } satisfies EnhancedProductGroup

          groupById.set(mappedSubGroup.id, mappedSubGroup)
          return mappedSubGroup
        }) ?? [],
    } satisfies EnhancedProductGroup
    groupById.set(mappedGroup.id, mappedGroup)

    return mappedGroup
  })

  return {
    productGroups: mappedGroups,
    configuredFamilies,
    familyByVariantId,
    familyById,
    variantById,
    groupById,
  }
}

export async function makeConfigurationFromLegacyCode(
  pendingConfiguration: TruckConfiguration,
  tg3Code: EnhancedLegacyManCode,
  tmsMapping: EnhancedTg3ToMaicConversionResponse,
  targetApiConfiguration: FullApiConfiguration,
): Promise<{ configuration: TruckConfiguration; marketDetails: MarketDetails }> {
  const configuredFamilies: TruckConfiguration['configuredFamilies'] = {}
  const { productGroups, familyByVariantId, variantById } = convertProductGroups(tmsMapping.productGroups, configuredFamilies)
  const { nationalCodeGroups } = convertNationalCodes(
    targetApiConfiguration.orderLanguage ?? 'de',
    tmsMapping.nationalProductGroups,
    configuredFamilies,
  )

  // handle locks
  // selectedVariant: {
  //   id: mappedVariant.id,
  //   timestamp: Date.now(),
  // },

  const locks = tg3Code?.configuration?.locks
  if (locks && familyByVariantId) {
    for (const lock of locks) {
      const variantId = lock.code as EnhancedProductVariant['id']
      const family = familyByVariantId.get(variantId)
      if (family) {
        let configuredFamily = configuredFamilies[family.id]
        if (!configuredFamily) {
          configuredFamilies[family.id] = configuredFamily = {
            isLocked: false,
            printOptions: {},
          }
        }
        configuredFamily.isLocked = lock.state === 'CLOSED'
        configuredFamily.selectedVariant = {
          id: variantId,
          timestamp: Date.now(),
        }
      }
    }
  }

  const conversionInsightsLookup = mapTg3ConversionInsights(tmsMapping, variantById, familyByVariantId)

  console.log('Made configuration from legacy code:', {
    tg3Code,
    tmsMapping,
    conversionInsightsLookup,
  })

  return {
    configuration: {
      ...pendingConfiguration,
      uuid: undefined,
      title: `TG3 ${tg3Code.code || 'unknown code'}`,
      version: 1 as ConfigurationVersion,
      ecmVersion: 1 as EcmConfigurationVersion,
      marketDenomination: tmsMapping.marketDenomination,
      productGroups,
      configuredFamilies,
      lastManCodeUpdate: {
        when: tg3Code.updateTimestamp ? Date.parse(tg3Code.updateTimestamp) : Date.now(),
      },
      lastChange: {
        version: 1,
        when: tg3Code.configuration?.configurationTimestamp ? Date.parse(tg3Code.configuration.configurationTimestamp) : Date.now(),
      },
      step: {
        stage: 'DetailedStep',
        view: 'configuration/step-1',
        requested: makeRequestConfigurationStep('DetailedStep'),
        system: 'DetailedStep' as EnhancedApiConfigurationStep,
      },
      apiConfiguration: {
        ...targetApiConfiguration,
        mode: 'read-only',
        desiredDealerDate: isValidDateString(tg3Code?.configuration?.slr) ? tg3Code.configuration.slr : undefined,
        desiredDealerDateIsEstimate: false,
      },
      tg3: {
        tg3TargetApiConfiguration: targetApiConfiguration,
        tg3ConversionInsightsLookup: conversionInsightsLookup,
      },
    },
    marketDetails: { groups: nationalCodeGroups },
  }
}
